import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import {
  TextField,
  Typography,
  Box,
  Checkbox,
  Button as MuiButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { spacing } from "@mui/system";
import makeStyles from "@mui/styles/makeStyles";
import Table from "../../components/ReactTable/Table";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import { useSnackbarNotifications } from "../../hooks/snackbar";
import { validateOrderStatuses } from "../../constants";
import TrainingObjectsPickerModal from "../Employees/CreateAssignmentsModal";
import usePermissions from "../../redux/selectors/usePermissions";

const Button = styled(MuiButton)(spacing);

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative",
    alignItems: "stretch",
    flex: "1 0 auto"
  },
  whiteCheckbox: {
    color: "#ffffff",
    "&$checked": {
      color: "#ffffff"
    }
  },
  numericInput: {
    width: "80px"
  },
  footerRow: {
    display: "flex",
    flexDirection: "row"
  }
}));

const InputCell = ({
  cell: { value: initialValue },
  row,
  daysToCompleteChanged
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onChange = value => {
    setValue(value);
  };

  const onBlur = () => {
    daysToCompleteChanged(row.original, value);
  };

  const classes = useStyles();
  const t = useScopedTranslation(
    "validate.order.assignments.table.columns.daysToComplete"
  );
  const handleInputChanged = event => {
    if (!isNaN(event.target.value)) {
      const scoreInt = parseInt(event.target.value);
      onChange(isNaN(scoreInt) ? "" : scoreInt);
    }
  };

  return !row.original.custom ? (
    <TextField
      className={classes.numericInput}
      disabled={!row.original.checked}
      label={t("placeholder")}
      variant="standard"
      value={value}
      onChange={handleInputChanged}
      onBlur={onBlur}
    />
  ) : (
    <TextField
      className={classes.numericInput}
      disabled={true}
      label={t("placeholder")}
      value={value}
    />
  );
};

const CheckedCell = ({
  cell: { value: initialValue },
  row,
  checkedChanged
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    !row.original.custom && (
      <Checkbox
        checked={value}
        disabled={row.original.disabled}
        onChange={() => checkedChanged(row.original)}
      />
    )
  );
};

const CheckedHeader = ({ checkedAllChanged, data }) => {
  const classes = useStyles();

  return (
    <Checkbox
      color="default"
      className={classes.whiteCheckbox}
      checked={
        !data.find(
          training =>
            !training.checked && !training.custom && !training.disabled
        )
      }
      onChange={() => checkedAllChanged()}
    />
  );
};

const CompletedTable = ({ order }) => {
  const t = useScopedTranslation("validate.order.assignments");

  const trainings = order.orderAssignments.map(orderAssignment => ({
    id: orderAssignment.id,
    skillName: orderAssignment.custom
      ? t("custom")
      : (orderAssignment.skill && orderAssignment.skill.name) || "-",
    toName:
      (orderAssignment.trainingObject && orderAssignment.trainingObject.name) ||
      "-"
  }));

  const columns = t => [
    {
      Header: t("table.columns.skill.header"),
      accessor: "skillName",
      id: "skillName",
      disableSortBy: true
    },
    {
      Header: t("table.columns.courseTitle.header"),
      accessor: "toName",
      id: "toName",
      disableSortBy: true
    }
  ];

  const memoColumns = useMemo(() => columns(t), []);

  return (
    <Table
      data={trainings}
      columns={memoColumns}
      manualFilters={true}
      manualSortBy={true}
      showCheckbox={false}
      showSelectAllCheckbox={true}
    />
  );
};

const calculateAssignments = (order, t) => {
  const customAssignments = order.orderAssignments
    .filter(orderAssignment => orderAssignment.custom)
    .map(orderAssignment => ({
      skillName: t("custom"),
      toName: orderAssignment.trainingObject.name,
      trainingObjectId: orderAssignment.trainingObject.id,
      id: "custom_" + orderAssignment.trainingObject.id,
      surveyDetailId: null,
      checked: null,
      daysToComplete: orderAssignment.daysToComplete,
      disabled: false,
      custom: true
    }));
  return customAssignments;
};

const calculateTrainings = (order, t) => {
  const assignments = calculateAssignments(order, t);
  const trainings = order.validateOrderRecommendedUpskillTrainings
    .reduce((trainings, training) => {
      const trainingObjects = training.trainingObjects.map(trainingObject => ({
        skillName: training.skill.name,
        toName: trainingObject.name,
        trainingObjectId: trainingObject.id,
        surveyDetailId: training.surveyDetail.id,
        id: training.skill.id + "_" + trainingObject.id,
        checked: false,
        daysToComplete: trainingObject.daysToComplete,
        disabled: assignments.some(
          assignment => assignment.trainingObjectId === trainingObject.id
        )
      }));
      return [...trainings, ...trainingObjects];
    }, [])
    .reduce(
      (tos, item) => (tos.find(to => to.id === item.id) ? tos : [...tos, item]),
      []
    );
  return trainings;
};

const ReviewTable = ({
  order,
  assignOrder,
  assignCustomAssignments,
  isUpdatingOrder,
  reviewOrder
}) => {
  const { validateCustomAssignmentsFeature } = usePermissions();
  const t = useScopedTranslation("validate.order.assignments");
  const classes = useStyles();
  const [recommendedTrainings, setRecommendedTrainings] = useState(
    calculateTrainings(order, t)
  );
  const customAssignments = calculateAssignments(order, t);

  const { displaySuccessMessage } = useSnackbarNotifications();
  const [createAssignmentModal, setCreateAssignmentModal] = useState(false);

  useEffect(() => {
    setRecommendedTrainings(calculateTrainings(order, t));
  }, [order]);

  const daysToCompleteChanged = (row, value) => {
    row.daysToComplete = value;
    setRecommendedTrainings(recommendedTrainings.map(training => training));
  };

  const checkedAllChanged = () => {
    if (
      recommendedTrainings.find(
        training => !training.checked && !training.disabled
      )
    ) {
      recommendedTrainings.forEach(training => (training.checked = true));
    } else {
      recommendedTrainings.forEach(training => (training.checked = false));
    }
    setRecommendedTrainings(recommendedTrainings.map(training => training));
  };

  const checkedChanged = row => {
    row.checked = !row.checked;
    setRecommendedTrainings(recommendedTrainings.map(training => training));
  };

  const columns = t => [
    {
      Header: CheckedHeader,
      accessor: "checked",
      Cell: CheckedCell,
      id: "checkbox",
      disableSortBy: true
    },
    {
      Header: t("table.columns.skill.header"),
      accessor: "skillName",
      id: "skillName",
      disableSortBy: true
    },
    {
      Header: t("table.columns.courseTitle.header"),
      accessor: "toName",
      id: "toName",
      disableSortBy: true
    },
    {
      Header: t("table.columns.daysToComplete.header"),
      accessor: "daysToComplete",
      Cell: InputCell,
      id: "daysToComplete",
      disableSortBy: true
    }
  ];

  const memoColumns = useMemo(() => columns(t), [recommendedTrainings]);

  const acceptedTrainings = recommendedTrainings.filter(
    training => training.checked
  );

  const canSave =
    acceptedTrainings.length &&
    acceptedTrainings.every(training =>
      Number.isInteger(training.daysToComplete)
    );

  const handleAssign = () => {
    if (canSave) {
      assignOrder(
        acceptedTrainings.map(training => ({
          trainingObjectId: training.trainingObjectId,
          daysToComplete: training.daysToComplete,
          surveyDetailId: training.surveyDetailId
        }))
      );
    }
  };
  const handleReview = () => {
    reviewOrder().then(() => {
      displaySuccessMessage(t("snackbar.reviewSuccessMsg"));
    });
  };

  const getRowTooltipProps = row =>
    row.original.disabled
      ? {
          title: t("disabledTooltipMessage"),
          showTooltipOnMouseEntryPoint: true,
          hideArrow: true,
          interactive: false,
          margin: 0
        }
      : null;

  return (
    <>
      <Table
        data={[...recommendedTrainings, ...customAssignments]}
        columns={memoColumns}
        manualFilters={true}
        manualSortBy={true}
        showCheckbox={false}
        showSelectAllCheckbox={true}
        daysToCompleteChanged={daysToCompleteChanged}
        checkedChanged={checkedChanged}
        checkedAllChanged={checkedAllChanged}
        rowSelectionDisabled={({ original: { disabled } }) => disabled}
        getRowTooltipProps={getRowTooltipProps}
      />
      <TrainingObjectsPickerModal
        onClose={() => setCreateAssignmentModal(false)}
        open={createAssignmentModal}
        employeeID={order.adjustedEmployee?.id || order.surveyUser?.id}
        assignCustomAssignments={assignCustomAssignments}
        isCreatingValidateAssignments={isUpdatingOrder}
        validate={true}
      />
      <Box className={classes.footerRow} mt={5}>
        <div className="flex-spacer" />
        <Button
          color="primary"
          variant="contained"
          onClick={handleAssign}
          disabled={!canSave}
        >
          {t("assign")}
        </Button>
        {validateCustomAssignmentsFeature && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => setCreateAssignmentModal(true)}
            ml={2}
            style={{ textTransform: "none" }}
          >
            {t("addCustomAssignments")}
          </Button>
        )}
        <Button
          color="primary"
          onClick={handleReview}
          variant="contained"
          ml={2}
        >
          {t("review")}
        </Button>
      </Box>
    </>
  );
};

const ValidateOrderAssignments = ({
  order,
  assignOrder,
  assignCustomAssignments,
  isUpdatingOrder,
  reviewOrder
}) => {
  const classes = useStyles();
  const t = useScopedTranslation("validate.order.assignments");

  const hasEmployee = !!(order.adjustedEmployee || order.surveyUser);
  const canEdit =
    (order.status === validateOrderStatuses.readyForReview ||
      order.status === validateOrderStatuses.readyForSupervisorReview) &&
    hasEmployee;

  return (
    <Box
      mt={{ xs: 5, sm: 4 }}
      className={classes.root}
      display="flex"
      flexDirection="column"
      flexGrow="1"
    >
      {order.status === validateOrderStatuses.completed && (
        <CompletedTable order={order} />
      )}
      {canEdit && (
        <ReviewTable
          order={order}
          assignOrder={assignOrder}
          assignCustomAssignments={assignCustomAssignments}
          reviewOrder={reviewOrder}
          isUpdatingOrder={isUpdatingOrder}
        />
      )}
      {!hasEmployee && order.status !== validateOrderStatuses.completed && (
        <Typography>{t("noEmployee")}</Typography>
      )}
    </Box>
  );
};

const ValidateOrderAssignmentsContainer = ({
  order,
  assignOrder,
  assignCustomAssignments,
  isUpdatingOrder,
  reviewOrder,
  isLoading
}) => {
  return (
    <>
      {isLoading || !order ? (
        <LoadingSpinner />
      ) : (
        <ValidateOrderAssignments
          order={order}
          assignOrder={assignOrder}
          assignCustomAssignments={assignCustomAssignments}
          reviewOrder={reviewOrder}
          isUpdatingOrder={isUpdatingOrder}
        />
      )}
    </>
  );
};

ValidateOrderAssignmentsContainer.propTypes = {
  order: PropTypes.object,
  assignOrder: PropTypes.func,
  reviewOrder: PropTypes.func,
  isLoading: PropTypes.bool.isRequired
};

export default ValidateOrderAssignmentsContainer;
